import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface Shop {
  shopId: string;
  shopName: string;
  /** ADP-66: the Shop's tenant Workspace. The ShopSwitcher + currentShopId are
   *  scoped to the active Workspace (design D4). */
  workspaceId: string;
  role: "owner" | "admin" | "member";
  permissions: string[];
  /** Ads account type; vendors (1P) are attributed over 14 days, sellers over 7 (#671). */
  accountType?: "seller" | "vendor" | null;
}

export interface WorkspaceView {
  id: string;
  name: string;
  role: "owner" | "admin" | "member";
  isActive: boolean;
  shopCount: number;
}

export interface User {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  // ADP-167: main account (owner) vs sub-account. Sub-accounts carry the
  // module keys they may access (dashboard|campaigns|ai|approvals|settings);
  // `null` means unrestricted (main account).
  isMainAccount: boolean;
  // ADP-180: explicit workspace role — `admin` is a backstage-promoted
  // sub-account with full permissions; isMainAccount stays owner-only.
  role?: "owner" | "admin" | "member" | null;
  modulePermissions: string[] | null;
  // ADP-66: Workspaces the User is a Member of + the active one (camelCase,
  // matching the api's `buildAuthUserPayload`).
  activeWorkspaceId?: string | null;
  workspaces?: WorkspaceView[];
  // ADP-261: set by the server; "demo" marks the sales demo deployment.
  deploymentMode?: "standard" | "demo";
}

interface AuthState {
  user: User | null;
  shops: Shop[];
  /**
   * ADP-66: the last-selected Shop per Workspace, persisted so a Workspace
   * switch restores the Shop the user was viewing in that Workspace (design
   * D4). `currentShopId` below is the reconciled runtime value for the active
   * Workspace.
   */
  currentShopByWorkspace: Record<string, string>;
  currentShopId: string | null;
  /**
   * ADP-110: the analytics shop scope (multi-select) per Workspace. This is a
   * VIEW filter for the aggregated dashboard reads only — operational pages
   * keep the single `currentShopId`. "All shops" is stored as NO key so newly
   * added shops automatically join the scope; only proper subsets are stored.
   * Resolve the effective scope with `resolveScopeShopIds`.
   */
  selectedShopIdsByWorkspace: Record<string, string[]>;

  isAuthenticated: () => boolean;

  setUser: (user: User) => void;
  setShops: (shops: Shop[]) => void;
  setCurrentShop: (shopId: string) => void;
  clearCurrentShop: () => void;
  setSelectedShopIds: (shopIds: string[]) => void;
  logout: () => void;
}

/**
 * Pick the active Workspace's current Shop: the persisted choice for that
 * Workspace when it's still a member of the fresh shop list, else the first
 * Shop of the active Workspace, else null. Never falls back to a Shop of
 * another Workspace — the ShopSwitcher is Workspace-scoped.
 */
/**
 * ADP-110: the effective analytics scope for the active Workspace — the stored
 * subset filtered to still-existing shops; missing/empty/fully-stale → ALL of
 * the workspace's shops (the scope is never empty while shops exist).
 */
export function resolveScopeShopIds(
  user: User | null,
  shops: Shop[],
  selectedShopIdsByWorkspace: Record<string, string[]>,
): string[] {
  const activeWs = user?.activeWorkspaceId ?? null;
  if (!activeWs) return [];
  const wsShopIds = shops.filter((s) => s.workspaceId === activeWs).map((s) => s.shopId);
  const stored = selectedShopIdsByWorkspace[activeWs];
  if (!stored || stored.length === 0) return wsShopIds;
  const valid = stored.filter((id) => wsShopIds.includes(id));
  return valid.length > 0 ? valid : wsShopIds;
}

function reconcileCurrentShop(
  user: User | null,
  shops: Shop[],
  currentShopByWorkspace: Record<string, string>,
): string | null {
  const activeWs = user?.activeWorkspaceId ?? null;
  if (!activeWs) return null;
  const wsShops = shops.filter((s) => s.workspaceId === activeWs);
  if (wsShops.length === 0) return null;
  const preferred = currentShopByWorkspace[activeWs];
  if (preferred && wsShops.some((s) => s.shopId === preferred)) return preferred;
  return wsShops[0]!.shopId;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      shops: [],
      currentShopByWorkspace: {},
      currentShopId: null,
      selectedShopIdsByWorkspace: {},
      isAuthenticated: () => get().user !== null,

      setUser: (user) =>
        set((state) => {
          const currentShopId = reconcileCurrentShop(
            user,
            state.shops,
            state.currentShopByWorkspace,
          );
          return { user, currentShopId };
        }),
      setShops: (shops) =>
        set((state) => {
          const currentShopId = reconcileCurrentShop(
            state.user,
            shops,
            state.currentShopByWorkspace,
          );
          return { shops, currentShopId };
        }),
      setCurrentShop: (shopId) =>
        set((state) => {
          const activeWs = state.user?.activeWorkspaceId;
          // Remember the choice per-Workspace so switching back restores it.
          const currentShopByWorkspace = activeWs
            ? { ...state.currentShopByWorkspace, [activeWs]: shopId }
            : state.currentShopByWorkspace;
          return { currentShopId: shopId, currentShopByWorkspace };
        }),
      clearCurrentShop: () =>
        set((state) => {
          const activeWs = state.user?.activeWorkspaceId;
          const currentShopByWorkspace = { ...state.currentShopByWorkspace };
          if (activeWs) delete currentShopByWorkspace[activeWs];
          return { currentShopId: null, currentShopByWorkspace };
        }),
      setSelectedShopIds: (shopIds) =>
        set((state) => {
          const activeWs = state.user?.activeWorkspaceId;
          if (!activeWs) return {};
          const wsShopIds = new Set(
            state.shops.filter((s) => s.workspaceId === activeWs).map((s) => s.shopId),
          );
          const valid = [...new Set(shopIds)].filter((id) => wsShopIds.has(id)).sort();
          const selectedShopIdsByWorkspace = { ...state.selectedShopIdsByWorkspace };
          // Full set (or cleared) → "all": drop the key so future shops auto-join.
          if (valid.length === 0 || valid.length === wsShopIds.size) {
            delete selectedShopIdsByWorkspace[activeWs];
          } else {
            selectedShopIdsByWorkspace[activeWs] = valid;
          }
          return { selectedShopIdsByWorkspace };
        }),
      logout: () =>
        set({
          user: null,
          shops: [],
          currentShopId: null,
        }),
    }),
    {
      name: "adpilot-auth",
      partialize: (state) => ({
        // Per-Workspace last Shop survives reloads; the active Shop is reconciled
        // from it + the fresh /auth/me payload on hydrate. user/shops are not
        // persisted — fetched fresh on each login.
        currentShopByWorkspace: state.currentShopByWorkspace,
        // ADP-110: the analytics scope survives reloads the same way.
        selectedShopIdsByWorkspace: state.selectedShopIdsByWorkspace,
      }),
    },
  ),
);
